import type { QueryClient } from "@tanstack/react-query";

import type { JobRun } from "@/hooks/useActivity";
import type { UserMe } from "@/hooks/useCurrentUser";
import apiClient from "@/lib/api-client";
import type { PaperListResponse, PaperWithRole } from "@/types/paper";

/** Trawl and snowball run in the page; with durable jobs on (slice 1c) they
 * report to /jobs so a closed page leaves "stopped at 9 of 14 seeds" and
 * Resume knows which seeds are left. Reporting never throws into the job. */

const REPORT_EVERY_MS = 2_000;
// Under the server's 90 s "page closed" heartbeat, with room for a slow seed.
const HEARTBEAT_MS = 30_000;

export function durableJobsOn(qc: QueryClient): boolean {
  return !!qc.getQueryData<UserMe>(["user", "me"])?.features?.durable_jobs;
}

export class PageRun {
  readonly id: string;
  readonly doneSeedIds: string[];
  private conceptId: string;
  private total: number;
  private lastSent = 0;
  private timer: ReturnType<typeof setTimeout> | null = null;
  private heartbeat: ReturnType<typeof setInterval>;
  private ended = false;

  constructor(conceptId: string, id: string, doneSeedIds: string[], total: number) {
    this.conceptId = conceptId;
    this.id = id;
    this.doneSeedIds = doneSeedIds;
    this.total = total;
    this.heartbeat = setInterval(() => this.send(), HEARTBEAT_MS);
  }

  /** A seed is finished (its papers are saved): Resume won't redo it. */
  seedDone(seedId: string): void {
    if (!this.doneSeedIds.includes(seedId)) this.doneSeedIds.push(seedId);
    const wait = this.lastSent + REPORT_EVERY_MS - Date.now();
    if (wait <= 0) this.send();
    else this.timer ??= setTimeout(() => this.send(), wait);
  }

  touch(): void {
    this.send();
  }

  end(status: "done" | "failed" | "cancelled", text?: string): void {
    if (this.ended) return;
    this.ended = true;
    clearInterval(this.heartbeat);
    if (this.timer) clearTimeout(this.timer);
    this.patch({
      done: this.doneSeedIds.length,
      done_seed_ids: this.doneSeedIds,
      status,
      ...(status === "failed" ? { error: text?.slice(0, 300) } : { summary: text?.slice(0, 300) }),
    });
  }

  private send(): void {
    if (this.ended) return;
    if (this.timer) clearTimeout(this.timer);
    this.timer = null;
    this.lastSent = Date.now();
    this.patch({ done: this.doneSeedIds.length, total: this.total, done_seed_ids: this.doneSeedIds });
  }

  private patch(json: Record<string, unknown>): void {
    void Promise.resolve()
      .then(() => apiClient.patch(`api/concepts/${this.conceptId}/jobs/${this.id}`, { json }))
      .catch(() => {});
  }
}

/** Open a run for this page-run job, or carry on a stopped one (Resume).
 * Null when durable jobs are off or the server can't be reached: the job
 * still runs, it just isn't recorded. */
export async function openPageRun(
  qc: QueryClient,
  conceptId: string,
  kind: "trawl" | "snowball",
  seedIds: string[],
  resume?: JobRun,
): Promise<PageRun | null> {
  if (!durableJobsOn(qc)) return null;
  try {
    if (resume) {
      const run = new PageRun(conceptId, resume.id, [...(resume.progress.done_seed_ids ?? [])],
        (resume.params.seed_ids as string[] | undefined)?.length ?? seedIds.length);
      // Report at once: the row reads "running" again and Resume goes away.
      run.touch();
      return run;
    }
    const res = await apiClient
      .post(`api/concepts/${conceptId}/jobs`, {
        json: { kind, total: seedIds.length, params: { seed_ids: seedIds } },
      })
      .json<{ id: string }>();
    return new PageRun(conceptId, res.id, [], seedIds.length);
  } catch {
    return null;
  }
}

/** The concept's papers already in the cache (Curate keeps its list there),
 * so Resume can find a stopped run's remaining seeds without a fetch. */
export function cachedPapers(qc: QueryClient, conceptId: string): PaperWithRole[] {
  const seen = new Map<string, PaperWithRole>();
  for (const [, data] of qc.getQueriesData<PaperListResponse>({ queryKey: ["papers", conceptId] })) {
    for (const p of data?.papers ?? []) seen.set(p.id, p);
  }
  return [...seen.values()];
}
